import { ReactNode } from "react";
import { Box, Link, Typography, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { AuroraGlow } from "./AuroraGlow";
import { AUTH_EASE, makeAuthColors } from "./authStyles";

/**
 * Shared shell for the ancillary auth pages (forgot/reset password, email
 * verification) — the same aurora field, logo placement and bottom-anchored
 * action column as AuthScreen, so the whole auth journey reads as one
 * surface. Changing `contentKey` crossfades between page states (form →
 * sent → error) instead of hard-swapping them.
 */

interface AuthPageLayoutProps {
  headline: string;
  supporting: ReactNode;
  /** Rendered above the headline — status badges, spinners. */
  topSlot?: ReactNode;
  /** Identity of the current page state; change it to animate a state swap. */
  contentKey?: string;
  children?: ReactNode;
}

export const AuthPageLayout = ({
  headline,
  supporting,
  topSlot,
  contentKey = "default",
  children,
}: AuthPageLayoutProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const { textPrimary, textSecondary } = makeAuthColors(isDark);

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.default,
        overflow: "hidden",
      }}
    >
      <AuroraGlow />

      <Box
        sx={{
          position: "relative",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          px: 3,
          width: "100%",
          maxWidth: 480,
          mx: "auto",
          pt: "calc(env(safe-area-inset-top, 0px) + 28px)",
          pb: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          "@keyframes authAlertIn": {
            from: { opacity: 0, transform: "translateY(-6px)" },
            to: { opacity: 1, transform: "none" },
          },
          "& .MuiAlert-root": { animation: "authAlertIn 0.3s ease" },
          "@media (prefers-reduced-motion: reduce)": {
            "& .MuiAlert-root": { animation: "none" },
          },
        }}
      >
        {/* Larger screens: matching spacer above centers the whole group. */}
        <Box sx={{ flex: { xs: 0, sm: 1 } }} />

        <motion.div
          initial={reduced ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: AUTH_EASE }}
        >
          <Box
            component="img"
            src={isDark ? "/insightLongLogoWhite.svg" : "/insightLongLogoPrimary.svg"}
            alt="Insight Analytics"
            sx={{ height: { xs: 28, sm: 32 }, width: "auto", display: "block", mx: { xs: 0, sm: "auto" } }}
          />
        </motion.div>

        <Box sx={{ flex: { xs: 1, sm: 0 }, minHeight: { sm: 40 } }} />

        <AnimatePresence mode="wait">
          <motion.div
            key={contentKey}
            initial={reduced ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduced ? undefined : { opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: AUTH_EASE }}
          >
            {topSlot}
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "2.25rem",
                lineHeight: 1.1,
                letterSpacing: "-0.03em",
                color: textPrimary,
                textAlign: { xs: "left", sm: "center" },
              }}
            >
              {headline}
            </Typography>
            <Typography
              sx={{
                mt: 1,
                mb: 3,
                fontSize: "1rem",
                lineHeight: 1.5,
                color: textSecondary,
                textAlign: { xs: "left", sm: "center" },
              }}
            >
              {supporting}
            </Typography>
            {children}
          </motion.div>
        </AnimatePresence>

        {/* Larger screens: matching spacer below centers the action column;
            legal links sit past it, pinned toward the screen's bottom edge. */}
        <Box sx={{ flex: { xs: 0, sm: 1 } }} />

        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Link
            component="button"
            onClick={() => navigate("/terms")}
            sx={{ color: textSecondary, textDecoration: "none", fontSize: "0.75rem" }}
          >
            Terms of Service
          </Link>
          <Typography component="span" sx={{ mx: 1, color: textSecondary, fontSize: "0.75rem" }}>
            ·
          </Typography>
          <Link
            component="button"
            onClick={() => navigate("/privacy-policy")}
            sx={{ color: textSecondary, textDecoration: "none", fontSize: "0.75rem" }}
          >
            Privacy Policy
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

const BADGE_TONES = {
  success: {
    bg: "rgba(34, 197, 94, 0.12)",
    border: "rgba(34, 197, 94, 0.25)",
    dark: "#4ade80",
    light: "#16a34a",
  },
  error: {
    bg: "rgba(239, 68, 68, 0.12)",
    border: "rgba(239, 68, 68, 0.25)",
    dark: "#f87171",
    light: "#dc2626",
  },
  warning: {
    bg: "rgba(245, 158, 11, 0.12)",
    border: "rgba(245, 158, 11, 0.25)",
    dark: "#fbbf24",
    light: "#d97706",
  },
} as const;

interface AuthStatusBadgeProps {
  tone: keyof typeof BADGE_TONES;
  children: ReactNode;
}

// Glass status circle for success/error states — springs in above the
// headline, aligned with the text column (left on phones, centered from sm).
export const AuthStatusBadge = ({ tone, children }: AuthStatusBadgeProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const reduced = useReducedMotion();
  const colors = BADGE_TONES[tone];

  return (
    <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "center" }, mb: 2.5 }}>
      <motion.div
        initial={reduced ? false : { scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 360, damping: 32 }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: colors.bg,
            border: `1px solid ${colors.border}`,
            backdropFilter: "blur(12px)",
            color: isDark ? colors.dark : colors.light,
          }}
        >
          {children}
        </Box>
      </motion.div>
    </Box>
  );
};
